import { ComponentType, SVGProps } from "react";
import {
  BuildingLibraryIcon,
  CreditCardIcon,
  DevicePhoneMobileIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";

export type FundingMethodId =
  | "bank-transfer"
  | "card"
  | "mobile-money"
  | "crypto";

export interface FundingMethod {
  id: FundingMethodId;
  label: string;
  description: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Every wallet currency this method appears for. */
  currencies: string[];
  /**
   * The subset of `currencies` the method is actually live for. Any currency
   * in `currencies` but not here renders as "coming soon" — so a method can be
   * ready for one currency (e.g. NGN bank transfer) while still pending for
   * another (e.g. USD bank transfer).
   */
  availableCurrencies: string[];
}

/**
 * Registry of wallet funding methods. This is the single source of truth for
 * the deposit drawer — ordering, per-currency support, and per-currency
 * availability all live here. To launch a method for a currency, add that
 * currency to `availableCurrencies` (and wire its panel in WalletDepositDrawer).
 *
 * TODO: hydrate/override this from `GET payments/methods/currency/:code` so
 * methods can be toggled server-side without a redeploy.
 */
export const fundingMethods: FundingMethod[] = [
  {
    id: "bank-transfer",
    label: "Bank transfer",
    description: "Fund with a dedicated account number",
    icon: BuildingLibraryIcon,
    currencies: ["NGN", "USD"],
    availableCurrencies: ["NGN"], // USD bank transfer is not live yet
  },
  {
    id: "card",
    label: "Card",
    description: "Pay with a debit or credit card",
    icon: CreditCardIcon,
    currencies: ["NGN", "GHS", "KES"],
    availableCurrencies: [],
  },
  {
    id: "mobile-money",
    label: "Mobile money",
    description: "MTN, M-Pesa, Airtel & more",
    icon: DevicePhoneMobileIcon,
    currencies: ["GHS", "KES", "UGX"],
    availableCurrencies: [],
  },
  {
    id: "crypto",
    label: "Crypto / stablecoin",
    description: "Deposit USDC and other assets",
    icon: CurrencyDollarIcon,
    currencies: ["USD"],
    availableCurrencies: [],
  },
];

/** Funding methods applicable to a given wallet currency, in registry order. */
export function getFundingMethodsForCurrency(
  currency: string | null,
): FundingMethod[] {
  if (!currency) return [];
  return fundingMethods.filter((method) =>
    method.currencies.includes(currency),
  );
}

/** Whether a method is live (has a real flow) for the given wallet currency. */
export function isMethodAvailable(
  method: FundingMethod,
  currency: string | null,
): boolean {
  return !!currency && method.availableCurrencies.includes(currency);
}

export function getFundingMethod(
  id: FundingMethodId,
): FundingMethod | undefined {
  return fundingMethods.find((method) => method.id === id);
}
